import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';

import { FORM_TYPES } from '@techchange/components';
import { messages } from '../../../../../i18n';

import './QuizDisplayResultsFailMsg.scss';

const propTypes = {

	/**
	 * The associated quiz form to display.
	 */
	form: PropTypes.shape({

		/**
		 * The form ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The form type.
		 */
		type: PropTypes.oneOf([FORM_TYPES]).isRequired,

		/**
		 * True, if learners are allowed to submit multiple form entries.
		 */
		allow_multiple_submissions: PropTypes.bool,

		/**
		 * The score required to pass the quiz.
		 */
		quiz_result_pass_percentage: PropTypes.number,

		/**
		 * The list of form fields of the form.
		 */
		form_fields: PropTypes.arrayOf(PropTypes.shape({
			id: PropTypes.string.isRequired,
		})).isRequired,

	}).isRequired,

	/**
	 * The form entry for the current user.
	 */
	formEntry: PropTypes.shape({

		/**
		 * True, if the current user has passed the quiz.
		 */
		quiz_result_passed: PropTypes.bool,

		/**
		 * The current user's score for the quiz (% value from 0 to 100).
		 */
		quiz_result_percentage: PropTypes.number,

	}).isRequired,

	/**
	 * Callback function to handle retrying the quiz with a new attempt.
	 */
	onRetry: PropTypes.func,

};

const QuizDisplayResultsFailMsg = ({ form, formEntry, onRetry }) => {

	const alertRef = useRef();

	useEffect(() => {

		if (alertRef && alertRef.current) {
			alertRef.current.scrollIntoView({ behavior: 'smooth' });
		}

	}, []);

	// Calculate the total number of quiz questions
	const numQuestions = form.form_fields ? form.form_fields.length : 0;

	let score = parseFloat(formEntry.quiz_result_percentage);

	if (isNaN(score)) {
		score = 0;
	}

	// Calculate the number of quiz questions answered correctly by the current user
	const numCorrect = Math.round((score / 100) * numQuestions);

	return (
		<div
			ref={alertRef}
			className="tc-content-builder-quiz-display-fail-alert">
			<div>
				<i className="fas fa-xmark-circle" />
				<strong>
					{' '}
					<FormattedMessage
						{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_DISPLAY_RESULTS_MSG_TEXT_FAILED_QUIZ_PREFIX} />
					{' '}
				</strong>
				<FormattedMessage
					{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_DISPLAY_RESULTS_MSG_TEXT_FAILED_QUIZ} />
				<div className="tc-content-builder-quiz-display-score-container">
					<div className="tc-content-builder-quiz-display-my-score-container">
						<span className="tc-content-builder-quiz-display-my-score-label">
							<FormattedMessage
								{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_DISPLAY_RESULTS_MSG_TEXT_YOUR_SCORE} />
						</span>
						<span className="tc-content-builder-quiz-display-my-score-pct">
							{`${score}%`}
						</span>
						<span>
							<span className="tc-content-builder-quiz-display-my-score-num-correct">
								<FormattedMessage
									{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_DISPLAY_RESULTS_MSG_TEXT_NUM_CORRECT}
									values={{ numCorrect, numQuestions }} />
							</span>
							{' '}
							<span>
								<FormattedMessage
									{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_DISPLAY_RESULTS_MSG_TEXT_NUM_CORRECT_LABEL} />
							</span>
						</span>
					</div>
					<div className="tc-content-builder-quiz-display-required-score-container">
						<span className="tc-content-builder-quiz-display-required-score-label">
							<FormattedMessage
								{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_DISPLAY_RESULTS_MSG_TEXT_REQUIRED_SCORE} />
						</span>
						<span className="tc-content-builder-quiz-display-required-score-pct">
							{`${form.quiz_result_pass_percentage}%`}
						</span>
					</div>
				</div>
				{onRetry &&
					<Button
						color="primary"
						className="mt-3"
						onClick={onRetry}>
						<FormattedMessage
							{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_QUIZ_DISPLAY_RESULTS_MSG_TEXT_FAILED_QUIZ_RETRY_PROMPT} />
					</Button>
				}
			</div>
		</div>
	);

};

QuizDisplayResultsFailMsg.propTypes = propTypes;

export default QuizDisplayResultsFailMsg;
